import "../styles/Gallery.css";
import {usePeople, useReunions, useImgDescs, getImgOptions} from "../hooks/hooks_index";
import {useState, useEffect} from "react";
import {Loading, ImgZoom, useAuthUser, MediaImage} from '../components/components_index';

const NUM_SHOWN = 50;

export default function Gallery({children}) {
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: reunions, isLoading: reunionsLoading, error: reunionsError} = useReunions();
  const {data: image_infos, isLoading: imgLoading, error: imgError} = useImgDescs();

  const [filterShow, setFilterShow] = useState(false);
  const [numShown, setNumShown] = useState(NUM_SHOWN);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState("");
  const [uploadedByFilter, setUploadedByFilter] = useState("all");
  const [peopleFilter, setPeopleFilter] = useState([]);
  const [reunionFilter, setReunionFilter] = useState("none");
  const [startStopYear, setStartStopYear] = useState({start: 0, stop: 9999})
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  const sortedPeopleOptions = Object.entries(people).sort(
    ([, personA], [, personB]) => {
      const nameA = (
        personA.AKA || `${personA.f_name} ${personA.l_name}`
      ).toLowerCase();
      const nameB = (
        personB.AKA || `${personB.f_name} ${personB.l_name}`
      ).toLowerCase();

      return nameA.localeCompare(nameB);
    }
  );

  const showMore = () => {
    setNumShown((prev) => prev + NUM_SHOWN)
  }

  useEffect(() => {
    setGalleryLoading(true);
    setGalleryError("");

    const params = {};
    if (uploadedByFilter !== "all") {
      params.uploaded_by = uploadedByFilter;
    }
    if (peopleFilter.length !== 0) {
      params.people = peopleFilter;
    }
    if (reunionFilter !== "none") {
      params.reunion = reunionFilter;
    }
    if (startStopYear.start > 0 || startStopYear.stop < 9999) {
      params.dateFrom = startStopYear.start;
      params.dateTo = startStopYear.stop;
    }
    params.sortByDate = [
      sortField,
      sortDirection
    ];

    getImgOptions(params)
      .then((data) => {
        const filteredData = data.filter(
          image => image.uploaded_by !== "P000000" && !image.profilePic
        )
        setGalleryImages(filteredData);
        setGalleryLoading(false);
      })
      .catch((err) => {
        setGalleryError(err.message);
        setGalleryLoading(false);
      });
  }, [uploadedByFilter, peopleFilter, reunionFilter, sortField, sortDirection, startStopYear]);

  if (galleryError, peopleError, reunionsError, imgError) return <div>Error Loading</div>;
  if (!people || !reunions || peopleLoading || reunionsLoading || imgLoading) return <Loading/>;

  return (
    <div className='gallery-page'>
      <div className="page-title">Family Photos</div>
      <GalleryFilters sortedPeopleOptions={sortedPeopleOptions} uploadedByFilter={uploadedByFilter} setUploadedByFilter={setUploadedByFilter}
        peopleFilter={peopleFilter} setPeopleFilter={setPeopleFilter} people={people} reunionFilter={reunionFilter} setReunionFilter={setReunionFilter} reunions={reunions}
        sortField={sortField} setSortField={setSortField} sortDirection={sortDirection} setSortDirection={setSortDirection}
        startStopYear={startStopYear} setStartStopYear={setStartStopYear} filterShow={filterShow} setFilterShow={setFilterShow} setNumShown={setNumShown}
      /> 
      <div className='gallery-container'>
        {galleryLoading && <Loading/>}
        {!galleryLoading &&
          <div className='gallery-list'>
            {galleryImages.slice(0,numShown).map((image) => {
              const imageSrc = image.media_id;
              const imageAlt = image.subtext || "Uploaded image";
              return (
                <div className={`gallery-item`} key={imageSrc} onClick={(e) => {
                  e.stopPropagation();
                }}>
                  <div className="gallery-img-container">
                    <ImgZoom src={imageSrc} alt={imageAlt} className={`gallery-image`} img_array={galleryImages}/>
                    {/* <MediaImage mediaID={imageSrc} alt={imageAlt} className={`gallery-image`}/> */}
                  </div>
                  {/* <div className="gallery-image-subtext">
                    <div>{imageAlt}</div> 
                  </div> */}
                </div>
              )
            })}
            {numShown < galleryImages.length && (
              <div className="show-more-container">
                <button onClick={showMore} className="show-more">Show More</button>
              </div>
            )}
            {galleryImages.length === 0 && (
              <p className="no-existing-images">
                No images match filter criteria
              </p>
            )}
          </div>
        }
      </div>
    </div>
  );
}

function GalleryFilters({
  sortedPeopleOptions, uploadedByFilter, setUploadedByFilter, 
  peopleFilter, setPeopleFilter, people, reunionFilter, setReunionFilter, reunions,
  sortField, setSortField, sortDirection, setSortDirection,
  startStopYear, setStartStopYear, filterShow, setFilterShow, setNumShown
}) {
  const {loginID} = useAuthUser();

  const handleAddPerson = (personID) => {
    if (!personID) return;
    setPeopleFilter((prev) => 
      prev.includes(personID) ? prev : [...prev, personID]
    );
  };

  const handleRemovePerson = (personID) => {
    setPeopleFilter((prev) => 
      prev.filter((id) => id !== personID)
    );
  };

  const handleClearFilters = () => {
    setUploadedByFilter("all");
    setPeopleFilter([])
    setReunionFilter("none");
    setSortField("date");
    setSortDirection("desc");
    setStartStopYear({start:0, stop:9999});
    setNumShown(NUM_SHOWN);
  }

  return (
    <div className="image-gallery-filters">
      <div className="filter-title" onClick={() => setFilterShow(!filterShow)}>
        <span>Filters</span>
        <span className="minimize-filters" >{filterShow?"\u2212":"+"}</span>
        {/* <span className="minimize-filters" >{filterShow?"(Hide)":"(Show)"}</span> */}
      </div>
            
      {filterShow &&
        <div className="visible-filters">
          <div className="filter-row">
            <div className="sub-filter">
              <label htmlFor="uploaded-by-filter">
                Uploaded By:
              </label>
              <select
                id="uploaded-by-filter"
                value={uploadedByFilter}
                onChange={(e) => setUploadedByFilter(e.target.value)}
              >
                <option value="all" className="all-images">
                  All Images
                </option>
                <option value={loginID} className="my-images">
                  My Images
                </option>
                {sortedPeopleOptions
                  .filter(([id]) => !id.startsWith("P0"))
                  .map(([id, person]) => (
                  <option
                    key={id}
                    value={id}
                  >
                    {person.AKA || `${person.f_name} ${person.l_name}`}
                  </option>
                ))}
              </select>
            </div>

            <div className="sub-filter">    
              <label htmlFor="sort-field">
                Sort By:
              </label>
              <select
                id="sort-field"
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
              >
                <option value="date">
                  Photo Taken
                </option>
                <option value="saved_date">
                  Upload Date
                </option>
              </select>

              <select
                id="sort-direction"
                value={sortDirection}
                onChange={(e) => setSortDirection(e.target.value)}
              >
                <option value="desc">
                  Newest First
                </option>
                <option value="asc">
                  Oldest First
                </option>
              </select>
            </div>

            <div className="sub-filter">
              <label htmlFor="reunion-filter">
                Reunion:
              </label>
              <select
                id="reunion-filter"
                value={reunionFilter}
                onChange={(e) => setReunionFilter(e.target.value)}
              >
                <option value="none" className="not-reunion">
                  No Filter
                </option>
                {/* <option value={loginID} className="all-reunions">
                  My Images
                </option> */}
                {Object.entries(reunions)
                  .sort(([, a], [, b]) => Number(b.year) - Number(a.year))
                  .filter(([_, reunion]) => reunion.year > 1905)
                  .map(([id, reunion]) => (
                  <option
                    key={id}
                    value={id}
                  >
                    {reunion.year}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="filter-row">
            <label htmlFor="people-filter">
              People:
            </label>
            <select 
              id="people-filter"
              value=""
              onChange={(e) => {
                handleAddPerson(e.target.value);
                e.target.value = "";
              }}
            >
              <option value="">
                Select a person...
              </option>
              {sortedPeopleOptions
                .filter(([id]) => !peopleFilter.includes(id) && !id.startsWith("P0"))
                .map(([id, person]) => (
                  <option key={id} value={id}>
                    {person.AKA || `${person.f_name} ${person.l_name}`}
                  </option>
                ))
              }
            </select>
            {peopleFilter.length > 0 && (
              <div className="selected-people">
                {/* <div>selected people</div> */}
                {peopleFilter.map((id) => {
                  const person = people[id];
                  if (!person) return null;
                  return (
                    <div key={id} className="selected-person">
                      <button className="selected-person-button" onClick={() => handleRemovePerson(id)}>{person.AKA || `${person.f_name} ${person.l_name}`}</button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="filter-row">
            <label htmlFor="start-filter">
              Taken Between:
            </label>
            <input
              id="start-filter"
              type="text"
              placeholder="Year"
              value={startStopYear.start === 0 ? "" : startStopYear.start}
              onChange={(e) => {
                const value = e.target.value.trim()
                setStartStopYear(prev => ({
                  ...prev, 
                  "start": /^\d+$/.test(value) ? parseInt(value, 10) : 0,
                }));
              }}
            />
            <label htmlFor="stop-filter" className="hyphen-label">
              -
            </label>
            <input
              id="stop-filter"
              type="text"
              placeholder="Year"
              value={startStopYear.stop === 9999 ? "" : startStopYear.stop}
              onChange={(e) => {
                const value = e.target.value.trim()
                setStartStopYear(prev => ({
                  ...prev, 
                  "stop": /^\d+$/.test(value) ? parseInt(value, 10) : 9999,
                }));
              }}
            />
          </div>

          <div className="filter-row">
            <button className="clear-filters" onClick={handleClearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      }
    </div>
  )
}